import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, NgZone, signal } from "@angular/core";
import { DashLangService } from "../../../modules/home/dash/service/dash.lang.service";
import { persistDashViewPreferenceIfLoggedIn } from "../../../modules/home/dash/service/dash.view.preference";
import { DashService } from "../../../modules/home/dash/service/dash.service";
import { DashPanelService } from "../../../modules/home/dash/service/dash.panel.service";
import { StickyStatesBridge } from "../../../services/sticky.states.bridge";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25LoadingInlineStaticComponent } from "../../../modules/s25-loading/s25.loading.inline.static.component";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import {
    isPanelMoveDirection,
    type PanelMoveDirection,
} from "../../../modules/s25-movable/s25.panel.move.overlay.model";
import type { HomeDashCustomize } from "./panel/home.dash.panel.model";
import { HomeDashSectionTypeId, type HomeDashSectionModel } from "./section/home.dash.section.model";
import { HomeDashSectionComponent } from "./section/home.dash.section.component";

type DashLangDashContext = Awaited<ReturnType<typeof DashLangService.getLang>>;

type DashPanelKeyboardReorderHost = HTMLElement & {
    _dashPanelKeyboardReorderHandler?: (event: KeyboardEvent) => void;
};

@TypeManagerDecorator("s25-ng-home-dash")
@Component({
    selector: "s25-ng-home-dash",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, HomeDashSectionComponent, S25LoadingInlineStaticComponent],
    template: `
        <div class="s25-ng" [hidden]="isInactive()">
            <div class="dash">
                <h1 class="select2-hidden-accessible">Dashboard</h1>
                <div id="My25Live_table">
                    <div class="container-fluid">
                        <div class="row row-normalized-height">
                            @for (
                                section of sections();
                                track trackSection(sectionOrd, section);
                                let sectionOrd = $index;
                                let isLast = $last
                            ) {
                                <div class="dash-sections">
                                    <s25-ng-dash-section
                                        [section]="section"
                                        [isLast]="isLast"
                                        [customize]="customize"
                                        (resetDash)="performDashReset()"
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            @if (showLoading()) {
                <s25-ng-loading-inline-static
                    data-testid="loadingSpinner"
                    [text]="loadingText()"
                ></s25-ng-loading-inline-static>
            }
        </div>
    `,
})
export class HomeDashComponent {
    private readonly ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dashService = inject(DashService);

    /** Sticky ui-router view: hidden while another home tab is active (replaces homeDash ng-show). */
    readonly isInactive = signal(false);

    /** Mirrors {@link KeyboardSortableService.lastElementButton} selecting chrome. */
    private lastKeyboardSortHandle: HTMLElement | null = null;

    /** Suppresses blur cleanup while keyboard reorder replaces panel header DOM nodes. */
    private keyboardReorderInProgress = false;

    readonly bootstrap = s25Resource(async () => {
        const lang = await DashLangService.getLang();
        void DashPanelService.preloadTasks();
        const dash = await this.dashService.get();
        return { lang, dash };
    });

    readonly resetting = signal(false);

    /** Bumped after keyboard reorder so OnPush section children receive new section/panel array refs. */
    private readonly panelOrderRevision = signal(0);

    readonly pageDash = computed(() => this.bootstrap.value()?.dash);

    readonly sections = computed(() => {
        this.panelOrderRevision();
        return this.pageDash()?.sections ?? [];
    });

    readonly showLoading = computed(() => {
        if (this.bootstrap.error()) {
            return false;
        }
        return this.bootstrap.isLoading() || this.resetting() || !this.pageDash();
    });

    readonly loadingText = computed(() => {
        const langDash = this.bootstrap.lastValue()?.lang as DashLangDashContext | undefined;
        return langDash?.dash?.text?.loading ?? "Loading...";
    });

    readonly customize: HomeDashCustomize = {
        toolboxOpen: signal(false),
        selectedMovePanelId: signal<number | undefined>(undefined),
        onFocus: (event: FocusEvent) => this.onCustomizeFocus(event),
        onFocusOut: (event: FocusEvent) => this.onCustomizeFocusOut(event),
        moveSelectedPanel: (direction: PanelMoveDirection) => this.moveSelectedPanel(direction),
    };

    constructor() {
        const unsubscribeSticky = StickyStatesBridge.subscribe((inactiveMap) => {
            this.ngZone.run(() => {
                this.isInactive.set(!!inactiveMap.dash);
                if (!this.isInactive()) {
                    persistDashViewPreferenceIfLoggedIn();
                }
            });
        });
        this.destroyRef.onDestroy(unsubscribeSticky);
        persistDashViewPreferenceIfLoggedIn();
    }

    trackSection(sectionOrd: number, section: HomeDashSectionModel): string {
        return `${section.sectionId ?? sectionOrd}-${section.typeId}`;
    }

    /** Persists reset on the server then reloads the page (legacy dashboard behavior). */
    async performDashReset(): Promise<void> {
        this.ngZone.run(() => this.resetting.set(true));
        try {
            await this.dashService.reset();
            if (!window.unitTesting) {
                window.location.reload();
            }
        } finally {
            this.ngZone.run(() => this.resetting.set(false));
        }
    }

    private onCustomizeFocus(event: FocusEvent): void {
        // Keyboard reorder works whenever a handle is focused — via Tab (toolbox open → tabindex 0)
        // or click (toolbox closed → tabindex -1 still focusable). Do not skip all keys when the
        // toolbox is closed; only skip moves whose destination is the hidden toolbox.
        if (this.keyboardReorderInProgress) {
            return;
        }

        const sortableElement = event.target;
        if (!(sortableElement instanceof HTMLElement)) {
            return;
        }

        sortableElement.closest("div.d-panel.section")?.classList.add("selecting");
        this.applyKeyboardSortSelectionChrome(sortableElement);

        const panelIdAttr = sortableElement.closest("[panel-id]")?.getAttribute("panel-id");
        const panelId = panelIdAttr != null ? parseInt(panelIdAttr, 10) : NaN;

        const dashSnapshot = this.pageDash();
        if (!dashSnapshot?.sections || Number.isNaN(panelId)) {
            return;
        }

        this.customize.selectedMovePanelId.set(panelId);
        this.attachPanelKeyboardReorderHandler(sortableElement as DashPanelKeyboardReorderHost, panelId);
    }

    /** Single-pointer path: move overlay buttons call the same reorder logic as arrow keys. */
    private moveSelectedPanel(direction: PanelMoveDirection): void {
        const panelId = this.customize.selectedMovePanelId();
        if (panelId == null || !Number.isFinite(panelId)) {
            return;
        }
        this.reorderPanel(panelId, direction);
    }

    private resolvePanelCoords(
        panelId: number,
        sortableSections: HomeDashSectionModel[] | undefined,
    ): { panelIndex: number; sectionIndex: number } | undefined {
        if (!sortableSections) {
            return undefined;
        }
        for (let secOrd = 0; secOrd <= sortableSections.length - 1; secOrd++) {
            const panelsRow = sortableSections[secOrd]?.panels ?? [];
            for (let panelOrd = 0; panelOrd <= panelsRow.length - 1; panelOrd++) {
                if (S25Util.propertyGet(panelsRow[panelOrd], "panelId") === panelId) {
                    return { sectionIndex: secOrd, panelIndex: panelOrd };
                }
            }
        }
        return undefined;
    }

    private isToolboxSection(section: HomeDashSectionModel | undefined): boolean {
        return section?.typeId === HomeDashSectionTypeId.Toolbox;
    }

    /** Click/drag cannot drop into a closed toolbox; keyboard and overlay buttons must match. */
    private isClosedToolbox(section: HomeDashSectionModel | undefined): boolean {
        return !this.customize.toolboxOpen() && this.isToolboxSection(section);
    }

    private findPanelKeyboardHandle(panelId: number): DashPanelKeyboardReorderHost | undefined {
        const panelRoot = document.querySelector(`div.d-panel.section[panel-id='${panelId}']`);
        if (!(panelRoot instanceof HTMLElement)) {
            return undefined;
        }
        const handle = panelRoot.querySelector(".sortable-handle");
        return handle instanceof HTMLElement ? (handle as DashPanelKeyboardReorderHost) : undefined;
    }

    private attachPanelKeyboardReorderHandler(sortableElement: DashPanelKeyboardReorderHost, panelId: number): void {
        const priorHandler = sortableElement._dashPanelKeyboardReorderHandler;
        if (priorHandler) {
            sortableElement.removeEventListener("keydown", priorHandler);
        }

        const onPanelReorderKeydown = (keydownEvent: KeyboardEvent): void => {
            if (!isPanelMoveDirection(keydownEvent.key)) {
                return;
            }
            keydownEvent.preventDefault();
            keydownEvent.stopPropagation();
            this.reorderPanel(panelId, keydownEvent.key);
        };

        sortableElement._dashPanelKeyboardReorderHandler = onPanelReorderKeydown;
        sortableElement.addEventListener("keydown", onPanelReorderKeydown);
    }

    /**
     * Shared reorder for keyboard arrows and the single-pointer move overlay.
     * Mutates section panel arrays in place, then refreshes the view and persists.
     */
    private reorderPanel(panelId: number, direction: PanelMoveDirection): void {
        const sortableSections = this.pageDash()?.sections;
        const coords = this.resolvePanelCoords(panelId, sortableSections);
        if (!coords || !sortableSections) {
            return;
        }
        const { sectionIndex, panelIndex } = coords;

        let didMove = false;

        if (this.isToolboxSection(sortableSections[sectionIndex])) {
            const firstDashSection = sortableSections.find(
                (section: HomeDashSectionModel) => !this.isToolboxSection(section),
            );
            if (!firstDashSection) {
                return;
            }
            const movedPanel = sortableSections[sectionIndex].panels.splice(panelIndex, 1);
            if (movedPanel?.length) {
                firstDashSection.panels.splice(0, 0, movedPanel[0]);
                didMove = true;
            }
        } else if (direction === "ArrowUp") {
            if (panelIndex - 1 < 0 && sectionIndex !== 0) {
                if (this.isClosedToolbox(sortableSections[0])) {
                    return;
                }
                const movedPanel = sortableSections[sectionIndex].panels.splice(panelIndex, 1);
                if (movedPanel?.length) {
                    sortableSections[0].panels.push(movedPanel[0]);
                    didMove = true;
                }
            } else if (sectionIndex !== 0) {
                const movedPanel = sortableSections[sectionIndex].panels.splice(panelIndex, 1);
                if (movedPanel?.length) {
                    sortableSections[sectionIndex].panels.splice(panelIndex - 1, 0, movedPanel[0]);
                    didMove = true;
                }
            }
        } else if (direction === "ArrowDown") {
            const currentSection = sortableSections[sectionIndex];
            const nextSection = sortableSections[sectionIndex + 1];
            if (panelIndex + 1 >= currentSection.panels.length && nextSection) {
                if (this.isClosedToolbox(nextSection)) {
                    return;
                }
                const movedPanel = currentSection.panels.splice(panelIndex, 1);
                if (movedPanel?.length) {
                    nextSection.panels.splice(0, 0, movedPanel[0]);
                    didMove = true;
                }
            } else if (
                !(sectionIndex === sortableSections.length - 1 && panelIndex + 1 > currentSection.panels.length)
            ) {
                const movedPanel = currentSection.panels.splice(panelIndex, 1);
                if (movedPanel?.length) {
                    currentSection.panels.splice(panelIndex + 1, 0, movedPanel[0]);
                    didMove = true;
                }
            }
        } else if (direction === "ArrowLeft") {
            const targetSection = sortableSections[sectionIndex - 1];
            if (this.isClosedToolbox(targetSection)) {
                return;
            }
            if (targetSection) {
                const movedPanel = sortableSections[sectionIndex].panels.splice(panelIndex, 1);
                if (movedPanel?.length) {
                    targetSection.panels.splice(0, 0, movedPanel[0]);
                    didMove = true;
                }
            }
        } else if (direction === "ArrowRight") {
            const targetSection = sortableSections[sectionIndex + 1];
            if (this.isClosedToolbox(targetSection)) {
                return;
            }
            if (targetSection) {
                const movedPanel = sortableSections[sectionIndex].panels.splice(panelIndex, 1);
                if (movedPanel?.length) {
                    targetSection.panels.splice(0, 0, movedPanel[0]);
                    didMove = true;
                }
            }
        }

        if (!didMove) {
            return;
        }

        this.keyboardReorderInProgress = true;
        this.customize.selectedMovePanelId.set(panelId);
        this.ngZone.run(() => {
            this.refreshPanelOrderView();
        });
        // Two deferred ticks: first lets Angular CD run; second ensures reordered panel DOM exists.
        this.scheduleInZone(() => {
            this.scheduleInZone(() => {
                this.reactivatePanelKeyboardHandle(panelId);
                this.scheduleInZone(() => {
                    this.keyboardReorderInProgress = false;
                });
                const dash = this.pageDash();
                if (dash) {
                    void this.dashService.putPanelsOrder(dash);
                }
            });
        });
    }

    /**
     * After reorder, Angular replaces panel DOM nodes — re-bind keydown on the live header element.
     */
    private reactivatePanelKeyboardHandle(panelId: number, allowRetry = true): void {
        const sortableElement = this.findPanelKeyboardHandle(panelId);
        const dashSnapshot = this.pageDash();
        if (!sortableElement || !dashSnapshot?.sections) {
            if (allowRetry) {
                this.scheduleInZone(() => this.reactivatePanelKeyboardHandle(panelId, false));
            }
            return;
        }

        sortableElement.closest("div.d-panel.section")?.classList.add("selecting");
        this.applyKeyboardSortSelectionChrome(sortableElement);
        this.customize.selectedMovePanelId.set(panelId);
        this.attachPanelKeyboardReorderHandler(sortableElement, panelId);
        sortableElement.focus();
    }

    private applyKeyboardSortSelectionChrome(sortableElement: HTMLElement): void {
        if (sortableElement.parentElement) {
            sortableElement.parentElement.classList.add("selectingElement");
        }
        if (this.lastKeyboardSortHandle && this.lastKeyboardSortHandle !== sortableElement) {
            this.lastKeyboardSortHandle.parentElement?.classList.remove("selectingElement");
        }
        this.lastKeyboardSortHandle = sortableElement;
    }

    /**
     * Keyboard reorder mutates panel arrays in place; clone section/panel refs so OnPush children re-render.
     */
    private refreshPanelOrderView(): void {
        const dash = this.pageDash();
        if (!dash?.sections) {
            return;
        }
        dash.sections = dash.sections.map((section: HomeDashSectionModel) => ({
            ...section,
            panels: [...section.panels],
        }));
        this.panelOrderRevision.update((revision) => revision + 1);
    }

    private onCustomizeFocusOut(event: FocusEvent): void {
        if (!event || this.keyboardReorderInProgress) {
            return;
        }
        const focusTarget = event.target;
        if (!(focusTarget instanceof HTMLElement)) {
            return;
        }

        // Moving focus into the move overlay (or related target still on the same panel) keeps selection.
        const relatedTarget = event.relatedTarget;
        if (relatedTarget instanceof Node) {
            const leavingPanel = focusTarget.closest("div.d-panel.section");
            const enteringOverlay =
                relatedTarget instanceof Element ? relatedTarget.closest(".s25-panel-move-overlay") : null;
            if (enteringOverlay && leavingPanel?.contains(enteringOverlay)) {
                return;
            }
        }

        const keyboardHost = focusTarget as DashPanelKeyboardReorderHost;
        const handler = keyboardHost._dashPanelKeyboardReorderHandler;
        if (handler) {
            keyboardHost.removeEventListener("keydown", handler);
            keyboardHost._dashPanelKeyboardReorderHandler = undefined;
        }
        if (this.lastKeyboardSortHandle === focusTarget) {
            focusTarget.parentElement?.classList.remove("selectingElement");
            this.lastKeyboardSortHandle = null;
        }
        focusTarget.closest("div.d-panel.section")?.classList.remove("selecting");

        const panelIdAttr = focusTarget.closest("[panel-id]")?.getAttribute("panel-id");
        const panelId = panelIdAttr != null ? parseInt(panelIdAttr, 10) : NaN;
        if (Number.isFinite(panelId) && this.customize.selectedMovePanelId() === panelId) {
            this.customize.selectedMovePanelId.set(undefined);
        }
    }

    /** Schedules work after the current call stack (replaces legacy `angBridge.$timeout`). */
    private scheduleInZone(callback: () => void, delayMs = 0): void {
        window.setTimeout(() => this.ngZone.run(callback), delayMs);
    }
}
